import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, useAnimation, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import SiteCTA from '@/components/SiteCTA';
import { ArrowRight, Clock, Users, TrendingUp, DollarSign, Zap, BarChart3, CheckCircle, Rocket, GraduationCap, Sparkles, ChevronDown } from 'lucide-react';

// Animated background component
const AnimatedBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black" />
      {/* Animated grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
      {/* Floating orbs */}
      <motion.div
        className="absolute top-20 right-20 w-96 h-96 bg-gold/20 rounded-full blur-3xl"
        animate={{
          x: [0, 100, 0],
          y: [0, -50, 0],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl"
        animate={{
          x: [0, -50, 0],
          y: [0, 100, 0],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </div>
  );
};

// Animated counter component
const AnimatedCounter = ({ value, duration = 2, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (inView) {
      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
        
        // Easing function for smooth animation
        const easeOutQuad = progress * (2 - progress);
        setCount(Math.floor(easeOutQuad * value));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(value);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [inView, value, duration]);
  
  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const AdHocServicesPage = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, -150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  // Service categories with icons
  const serviceCategories = [
    {
      title: 'Quick Wins & Targeted Solutions',
      description: 'On demand PMO support to address specific challenges impacting business results and organizational performance.',
      icon: Zap,
      color: 'from-gold to-amber-400',
      bgColor: 'from-gold/10 to-amber-400/10'
    },
    {
      title: 'PMO Transformation',
      description: 'Helping organizations build, elevate, and optimize PMOs for lasting value, measurable impact, and sustainable growth over time.',
      icon: Rocket,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-500/10 to-emerald-500/10'
    },
    {
      title: 'Fractional PMO Leadership',
      description: 'Expert Fractional leadership keeping your PMO stable through transitions & growth.',
      icon: Users,
      color: 'from-deepBlue to-blue-500',
      bgColor: 'from-deepBlue/10 to-blue-500/10'
    },
    {
      title: 'Project Leadership Coaching',
      description: 'Role specific coaching for PMO, Project, Program, and Portfolio leaders ready to elevate their impact and accelerate their career.',
      icon: GraduationCap,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'from-purple-500/10 to-indigo-500/10'
    }
  ];

  // Removed PMO Leadership Coaching data (section deprecated)

  const stats = [
    { value: 75000, label: 'Savings', icon: DollarSign, isCounter: true, prefix: '$', suffix: '' },
    { value: '3x', label: 'ROI', icon: TrendingUp, isCounter: false },
    { value: '30%', label: 'Cut in Delivery Time', icon: BarChart3, isCounter: false },
    { value: '1-3', label: 'Weeks to Impact', icon: Clock, isCounter: false }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <AnimatedBackground />
        
        {/* Hero content */}
        <motion.div 
          style={{ y, opacity }}
          className="container mx-auto px-4 relative z-10 text-center"
        >
          {/* Animated badge */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-6 py-3 mb-8 border border-gold/30 backdrop-blur-sm"
          >
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
            <span className="text-gold font-bold text-sm uppercase tracking-wider">PMO Services & Solutions</span>
            <Sparkles className="w-5 h-5 text-gold animate-pulse" />
          </motion.div>

          {/* Main heading with staggered animation */}
          <motion.div className="mb-8">
            <motion.h1 
              className="text-5xl md:text-7xl lg:text-8xl font-bold leading-tight"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-gold via-amber-300 to-gold animate-gradient">
                OptiGold PMO
              </span>
              <motion.span 
                className="block text-white mt-4"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Services
              </motion.span>
            </motion.h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            From rapid assessments to complete transformations, we deliver tailored PMO solutions that drive measurable business impact without the overhead of large consulting firms.
          </motion.p>

          {/* CTA Button removed per request */}
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 inset-x-0 flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col items-center gap-2 text-gold">
            <span className="text-sm uppercase tracking-wider">Scroll to explore</span>
            <ChevronDown className="w-6 h-6" />
          </div>
        </motion.div>
      </section>

        {/* Stats Section */}
        <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-3xl border border-gold/20 p-8">
                <h3 className="text-xl md:text-2xl font-semibold text-center mb-8">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                    Results We've Delivered
                  </span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.label}
                      className="text-center"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <motion.div
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <stat.icon className="w-8 h-8 text-gold mx-auto mb-3" />
                      </motion.div>
                      <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">
                        {stat.isCounter ? (
                          <AnimatedCounter 
                            value={stat.value} 
                            duration={2.5}
                            prefix={stat.prefix || ''}
                            suffix={stat.suffix || ''}
                          />
                        ) : (
                          stat.value
                        )}
                      </div>
                      <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Categories Overview */}
        <section className="py-20 bg-gradient-to-b from-black via-gray-900 to-black relative">
          <div className="container mx-auto px-4">
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-300">
                  How We Help
                </span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Four engagement models designed to meet you where you are.
              </p>
            </motion.div>

            <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {serviceCategories.map((category, index) => {
                // Determine the link path for each service category
                let linkPath = null;
                if (category.title === 'Quick Wins & Targeted Solutions') {
                  linkPath = '/services/quick-wins';
                } else if (category.title === 'PMO Transformation') {
                  linkPath = '/services/pmo-transformation';
                } else if (category.title === 'PMO Power. On Demand.') {
                  linkPath = '/services/precision-pmo';
                } else if (category.title === 'Fractional PMO Leadership') {
                  linkPath = '/services/fractional-pmo';
                } else if (category.title === 'Project Leadership Coaching') {
                  linkPath = '/services/project-leadership-coaching';
                }
                
                const CardWrapper = linkPath ? Link : 'div';
                const cardProps = linkPath ? { to: linkPath } : {};
                const idProps = category.title === 'Project Leadership Coaching' ? { id: 'project-leadership-coaching' } : {};
                
                return (
                  <CardWrapper key={category.title} {...cardProps} {...idProps}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ scale: 1.05 }}
                      className="relative h-full block"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${category.bgColor} rounded-2xl blur-xl opacity-50`} />
                      <div className="relative bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-xl rounded-2xl p-8 border border-gold/20 h-full group hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/20 transition-all flex flex-col">
                        <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center mb-6`}>
                          <category.icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-gold transition-colors leading-snug min-h-[56px]">{category.title}</h3>
                        <p className="text-gray-300 leading-relaxed min-h-[96px]">{category.description}</p>
                        {linkPath && (
                          <div className="mt-4 text-gold font-semibold flex items-center gap-2 mt-auto">
                            Explore Solutions <ArrowRight className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                    </motion.div>
                  </CardWrapper>
                );
              })}
            </div>
          </div>
        </section>

        {/* PMO Leadership Coaching Section removed per request */}

        {false && (
        <section className="py-20 relative">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto space-y-12">
              
              {/* Fractional PMO Leadership */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <div className="bg-gradient-to-br from-gold via-amber-400 to-gold rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden">
                  {/* Glossy overlay effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-white/20 pointer-events-none" />
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" />
                  
                  <div className="relative grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="inline-flex items-center gap-3 bg-black/10 rounded-full px-4 py-2 mb-6 border border-white/30">
                        <Users className="w-5 h-5 text-white" />
                        <span className="text-white font-semibold">Fractional Leadership</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                        Fractional PMO Leadership
                      </h2>
                      <p className="text-xl text-white mb-8">
                        Get the strategic guidance and hands-on leadership your PMO needs, 
                        without the cost of a full-time executive.
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      {[
                        'Part-time PMO leadership support',
                        'Ongoing portfolio oversight and guidance',
                        'Hands-on support with planning, performance, and delivery',
                        'Strategic partnership without the overhead'
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-black mt-0.5 flex-shrink-0" />
                          <span className="text-white">{item}</span>
                        </div>
                      ))}
                      
                      <div className="pt-6 mt-6 border-t border-white/30">
                        <p className="text-black font-semibold mb-2">PERFECT FOR</p>
                        <p className="text-white/95">
                          Organizations needing leadership continuity, PMO stability, or interim guidance during transitions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Comprehensive PMO Transformation */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
              >
                <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)'}}>
                  {/* High gloss effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-white/10 pointer-events-none" />
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32" />
                  
                  <div className="relative grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <div className="inline-flex items-center gap-3 bg-gradient-to-r from-gold/20 to-amber-400/20 rounded-full px-4 py-2 mb-6 border border-gold/30">
                        <Rocket className="w-5 h-5 text-gold" />
                        <span className="text-gold font-semibold">Transformation</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                        Comprehensive PMO Transformation
                      </h2>
                      <p className="text-xl text-white/90 mb-8">
                        Build a world-class PMO that drives strategic value, 
                        operational excellence, and competitive advantage.
                      </p>
                    </div>
                    
                    <div className="space-y-4">
                      {[
                        'Design or overhaul of PMO structure and services',
                        'Process standardization & governance frameworks',
                        'Long-range planning and capacity modeling',
                        'Implementation of tools, systems, and metrics'
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                          <span className="text-white/90">{item}</span>
                        </div>
                      ))}
                      
                      <div className="pt-6 mt-6 border-t border-gold/20">
                        <p className="text-gold font-semibold mb-2">PERFECT FOR</p>
                        <p className="text-white/80">
                          Organizations ready to build a PMO from scratch or elevate an existing PMO to world-class performance.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        )}

        {/* Removed "Why organizations choose us" and CTA sections per request */}

      <SiteCTA />
    </div>
  );
};

export default AdHocServicesPage;
